import { useAuth } from "@/lib/auth";
import { useSubscription } from "@/lib/subscription";
import { useI18n } from "@/lib/i18n";
import { Loader2, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import EmailAuthCard from "./EmailAuthCard";

/**
 * Access flow:
 *   1) Not signed in  → login card
 *   2) Signed in, has access (trial or paid) → children (full app)
 *   3) Signed in, NO access → route to /pricing FIRST. From /pricing user can
 *      click "Try demo" to opt into limited (2 pairs) mode; we set a flag in
 *      localStorage and then render children (Dashboard will filter itself).
 */
const DEMO_FLAG = "forex.demo_ack";

export function acceptedDemo() {
  try { return localStorage.getItem(DEMO_FLAG) === "1"; } catch { return false; }
}
export function acceptDemo() {
  try { localStorage.setItem(DEMO_FLAG, "1"); } catch { /* ignore */ }
}
export function resetDemoAck() {
  try { localStorage.removeItem(DEMO_FLAG); } catch { /* ignore */ }
}

export default function AccessGate({ children }) {
  const { user, loading: authLoading, login } = useAuth();
  const { loading, has_access } = useSubscription();
  const { t, dir } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authLoading || (user && loading)) return;
    if (!user) return;
    if (has_access) return;
    // No access + hasn't opted into demo → send to pricing
    if (!acceptedDemo() && location.pathname !== "/pricing") {
      navigate("/pricing", { replace: true });
    }
  }, [authLoading, loading, user, has_access, navigate, location.pathname]);

  if (authLoading || (user && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center px-6 text-zinc-100 relative">
        <div className={"absolute top-4 z-20 " + (dir === "rtl" ? "left-4" : "right-4")}>
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-md flex flex-col items-center">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300 mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {t("gate_perk_short") || "7-day free trial · No card required"}
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-zinc-400">
              {t("gate_title") || "Your edge in the market"}
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              {t("gate_sub_v3") || "Sign in or create an account to unlock 21 pairs, AI reasoning, and Personal Best Hours."}
            </p>
          </div>
          <EmailAuthCard onGoogleClick={login} />
        </div>
      </div>
    );
  }

  // Signed in — render children. Dashboard handles demo-mode UI on its own.
  return children;
}
